import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchAthleteTargets,
  fetchMaxesDetailed,
  isApiErrorLike,
  parseProgramSummaries,
  type AthleteTargets,
  type CoachVisibleProfile,
  type CoachCurrentMax,
  type ProgramRow,
} from "@heraion/shared";

import type { ProgressApiFetch } from "../Progress/types";
import { useAthleteSessions, type AthleteSessionsList } from "./useAthleteSessions";

// CO-9 athlete-detail data hook. Fetches ONLY what the detail surface needs
// beyond the panels' own reads: the athlete's profile projection (units /
// weekStartDay / pinned id), their programs (for the pinned block +
// prescription) and their current MAX map — all through the CO-11 edge-gated
// routes. The session-results + catalog reads are deliberately NOT here: the
// shared Progress panels fetch those themselves through the athlete-scoped
// ProgressDeps (one single-flight pair per athlete), so this hook never
// duplicates useCoachDashboard's fan-out.
//
// Fail-soft like the dashboard (CO-8b): a missing profile (athlete never
// opened Settings) or a hiccuping read degrades to safe defaults instead of
// sinking the whole screen.
export type AthleteDetailState = {
  loading: boolean;
  profile: CoachVisibleProfile | null;
  /** #343: the profile read failed. A 404 (no profile yet) is not a failure. */
  profileFailed: boolean;
  /** Reads the profile again, alone. */
  reloadProfile: () => void;
  // A program another coach wrote comes as its summary (#332).
  programs: ReadonlyArray<ProgramRow>;
  maxes: Map<string, CoachCurrentMax> | null;
  // ME-3: the athlete's own AthleteTargets (GET /api/me/targets?athleteSub) —
  // the coach scores the athlete's real goals, not the pinned program's
  // embedded targets. null while loading OR when the read failed.
  athleteTargets: AthleteTargets | null;
  /** #25: the athlete's sessions since the bond began, paged. */
  sessions: AthleteSessionsList;
};

export function useAthleteDetail(
  apiFetch: ProgressApiFetch,
  athleteSub: string,
): AthleteDetailState {
  const sessions = useAthleteSessions(apiFetch, athleteSub);
  const [state, setState] = useState<Omit<AthleteDetailState, "sessions" | "reloadProfile">>({
    loading: true,
    profile: null,
    profileFailed: false,
    programs: [],
    maxes: null,
    athleteTargets: null,
  });

  // The athlete the hook is showing now, null once unmounted: a reload that
  // answers for another one is dropped (#343).
  const liveSub = useRef<string | null>(null);
  // #343: two reloads of the SAME athlete can land out of order; only the
  // last one started applies (liveSub only orders different athletes).
  const reloadSeq = useRef(0);

  useEffect(() => {
    let alive = true;
    liveSub.current = athleteSub;
    // Every start takes a ticket, so a reload left in flight by an earlier
    // visit to this same athlete (A, B, A) can no longer match.
    const seq = ++reloadSeq.current;
    setState({
      loading: true,
      profile: null,
      profileFailed: false,
      programs: [],
      maxes: null,
      athleteTargets: null,
    });

    (async () => {
      const q = `athleteSub=${encodeURIComponent(athleteSub)}`;
      const [read, programs, maxes, athleteTargets] = await Promise.all([
        readProfile(apiFetch, athleteSub),
        apiFetch<unknown>(`/api/me/programs?${q}`)
          .then(parseProgramSummaries)
          .catch(() => [] as ProgramRow[]),
        fetchMaxesDetailed(apiFetch, athleteSub).catch(
          () => new Map<string, CoachCurrentMax>(),
        ),
        fetchAthleteTargets(apiFetch, athleteSub),
      ]);
      if (!alive) return;
      // A reload started meanwhile owns the profile: keep its answer.
      if (reloadSeq.current !== seq) {
        setState((prev) => ({ ...prev, loading: false, programs, maxes, athleteTargets }));
        return;
      }
      setState({ loading: false, ...read, programs, maxes, athleteTargets });
    })();

    return () => {
      alive = false;
      liveSub.current = null;
    };
    // `athleteSub` is the only real input — apiFetch is a stable per-surface
    // seam (and where the host scopes it per athlete, its identity tracks the
    // sub anyway). Widening the deps would re-fetch on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteSub]);

  const reloadProfile = useCallback(() => {
    const seq = ++reloadSeq.current;
    void readProfile(apiFetch, athleteSub).then((read) => {
      if (liveSub.current === athleteSub && reloadSeq.current === seq) setState((prev) => ({ ...prev, ...read }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteSub]);

  return { ...state, reloadProfile, sessions };
}

// Fail-soft: a 404 and a failed read both give no profile, and only the
// failure says so.
const readProfile = (
  apiFetch: ProgressApiFetch,
  athleteSub: string,
): Promise<{ profile: CoachVisibleProfile | null; profileFailed: boolean }> =>
  apiFetch<CoachVisibleProfile>(`/api/profiles/${encodeURIComponent(athleteSub)}`).then(
    (profile) => ({ profile, profileFailed: false }),
    (e: unknown) => ({ profile: null, profileFailed: !(isApiErrorLike(e) && e.status === 404) }),
  );
